/**
 * Ported VERBATIM from apr_template `src/pages/ResourceAllocation.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useState, useEffect, useRef } from 'react';
import PageHeader from '../compat/PageHeader';
import { authFetch } from '../compat/api';
import HelpPanel from '../compat/HelpPanel';
import { dispositionColor as matrixColor } from '../dispositionPalette';
import type { Help } from '../compat/helpTypes';
import './ApplicationDependencies.css';
// Decision Workspace design (owner ruling 2026-10-21): kit icons, panels and token pairs.
import { Banner, Icon, IconButton, Sheet, icons } from '@platform/ui-kit/workspace';
import { WS_CHIP, WS_COLOR, WS_RADIUS, WS_STATUS, wsTone, wsType, type WsTone } from '../workspace/ws';
import { useWsDialogs } from '../workspace/useWsDialogs';

const ResourceAllocationHelp: Help = {
  title: 'Resource Allocation',
  icon: '🧑‍💼',
  purpose:
    'Assign resource pools (in <strong>FTE</strong>) to each application&rsquo;s migration phases, turning capacity into a concrete staffing plan per integration group.',
  doHere: [
    'Click <strong>&#10133; Add Resource Allocation</strong>, pick a group and application, then enter FTE per phase.',
    'Choose the <strong>Resource Pool</strong> that supplies the effort; the table shows pool, location and FTE.',
    'Filter by grouping, application, phase, resource pool or status to focus the list.',
    'Bulk-load via <strong>Download Template</strong> then <strong>Upload File</strong>; <strong>Download Data</strong> exports allocations.',
    'Use <strong>&#128065;</strong> / <strong>&#9999;&#65039;</strong> / <strong>&#128465;&#65039;</strong> to view, edit or delete a record.',
  ],
  fits: 'Integration &rarr; Resource Planning &rarr; Resource Allocation',
  firstTime:
    'Pick an integration group and application, then enter the FTE each phase needs from a resource pool.',
  sections: [
    {
      type: 'defs',
      label: 'Migration phases',
      items: [
        { term: 'Assessment', color: '#1565c0', def: 'Assessment &amp; Planning.' },
        { term: 'Design', color: '#6a1b9a', def: 'Design &amp; Architecture.' },
        { term: 'Migration', color: '#e65100', def: 'Migration Execution.' },
        { term: 'Testing', color: '#00838f', def: 'Testing &amp; Validation.' },
        { term: 'Go-Live', color: '#2e7d32', def: 'Go-Live &amp; Optimization.' },
      ],
    },
  ],
  links: [
    { href: '/integration-framework/resource-capacity', label: 'Resource Capacity' },
    { href: '/integration-framework/role-utilization', label: 'Role Utilization' },
  ],
};

interface ResourceAllocationRecord {
  allocation_id: number;
  application_id_code: string;
  application_name?: string;
  phase_name: string;
  resource_pool_id: number | null;
  allocated_fte: number;
  integration_group_id?: string;
  grouping_id?: string;
  group_name?: string;
  created_at?: string;
  updated_at?: string;
}

interface ScheduleRecord {
  application_id: string;
  integration_group_id: string;
  schedule_status: string;
  deployment_status?: string;
}

interface ResourcePool {
  resource_pool_id: number;
  resource_type: string;
  available_capacity_percent: number;
  resource_location: 'On-Site' | 'Off-Shore';
}

interface Application {
  application_id: string;
  application_id_code?: string;
  application_name?: string;
  description?: string;
  disposition?: string;
  integration_group_id?: string;
}

interface IntegrationGroup {
  integration_group_id: string;
  integration_group_description: string;
  application_count: number;
}

// Helper function for disposition colors
// PORT NOTE: a disposition badge is a FILLED chip in the Disposition Matrix colour, with
// readable text on it (§1.9.3 rules 2-3) — not the source's hard-coded wash.
const getDispositionColor = (disposition: string | undefined) => {
  const known = matrixColor(disposition);
  // DW: "not in the matrix" is the palette's own fallback (was a literal grey hex); the
  // grey wash behind an unknown value is the kit's neutral pair.
  return disposition && known.base !== matrixColor(null).base
    ? { bg: known.base, color: known.onBase }
    : { bg: WS_STATUS.neutral.fill, color: WS_STATUS.neutral.ink };
};

const ResourceAllocation: React.FC = () => {
  // Standard phases used across the application
  const STANDARD_PHASES = [
    'Assessment_Planning',
    'Design_Architecture',
    'Migration_Execution',
    'Testing_Validation',
    'GoLive_Optimization'
  ];

  const [records, setRecords] = useState<ResourceAllocationRecord[]>([]);
  const [resourcePools, setResourcePools] = useState<ResourcePool[]>([]);
  const [applications, setApplications] = useState<Application[]>([]);
  const [singleFormApplications, setSingleFormApplications] = useState<Application[]>([]);
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [scheduleData, setScheduleData] = useState<ScheduleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAppDropdown, setShowAppDropdown] = useState(false);
  const [showSingleAppDropdown, setShowSingleAppDropdown] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [filterApplicationCode, setFilterApplicationCode] = useState<string>('');
  const [filterPhase, setFilterPhase] = useState<string>('');
  const [filterGroupingId, setFilterGroupingId] = useState<string>('');
  const [filterResourcePool, setFilterResourcePool] = useState<string>('');
  const [editingRecord, setEditingRecord] = useState<ResourceAllocationRecord | null>(null);
  const [viewOnlyMode, setViewOnlyMode] = useState(false);
  const [formData, setFormData] = useState({
    groupingId: '',
    applicationIdCode: '',
    phaseName: '',
    resourcePoolId: '',
    allocatedFte: ''
  });

  // New enhanced form data for bulk phase allocation
  const [bulkFormData, setBulkFormData] = useState({
    groupingId: '',
    applicationIdCode: '',
    resourcePoolId: '',
    allocations: {} as Record<string, string> // phase -> FTE mapping
  });

  const [showBulkForm, setShowBulkForm] = useState(false);
  const { confirm, notify, dialogs } = useWsDialogs();

  useEffect(() => {
    fetchRecords();
    fetchResourcePools();
    fetchIntegrationGroups();
    fetchScheduleData();
  }, []);

  // Load applications when grouping ID changes in bulk form
  useEffect(() => {
    if (bulkFormData.groupingId) {
      fetchApplicationsForGroup(bulkFormData.groupingId);
    } else {
      setApplications([]);
    }
  }, [bulkFormData.groupingId]);

  // Load applications when grouping ID changes in single form
  useEffect(() => {
    if (formData.groupingId) {
      fetchApplicationsForSingleForm(formData.groupingId);
    } else {
      setSingleFormApplications([]);
    }
  }, [formData.groupingId]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.custom-dropdown')) {
        setShowAppDropdown(false);
        setShowSingleAppDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchRecords = async () => {
    try {
      const response = await authFetch('/api/resource-allocation');
      // Don't treat 404 as an error - it just means no data
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to fetch');
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        // Sort by Allocation ID in ascending order
        data.sort((a: ResourceAllocationRecord, b: ResourceAllocationRecord) => a.allocation_id - b.allocation_id);
        setRecords(data);
      } else {
        // API returned non-array (empty or error), treat as empty
        setRecords([]);
      }
      setLoading(false);
    } catch (err: any) {
      // Only set error for actual failures, not empty data
      console.error('Error fetching resource allocation records:', err);
      setRecords([]);
      setLoading(false);
    }
  };

  const fetchResourcePools = async () => {
    try {
      const response = await authFetch('/api/resource-capacity');
      if (!response.ok) throw new Error('Failed to fetch resource pools');
      const data = await response.json();
      setResourcePools(data);
    } catch (err: any) {
    }
  };

  const fetchApplicationsForGroup = async (groupId: string) => {
    try {
      const response = await authFetch(`/api/application-dependencies/lookup/applications/${groupId}`);
      const result = await response.json();
      if (result.success) {
        setApplications(result.data || []);
      } else {
        setApplications([]);
      }
    } catch (err: any) {
      console.error('Error loading applications:', err);
      setApplications([]);
    }
  };

  const fetchApplicationsForSingleForm = async (groupId: string) => {
    try {
      const response = await authFetch(`/api/application-dependencies/lookup/applications/${groupId}`);
      const result = await response.json();
      if (result.success) {
        setSingleFormApplications(result.data || []);
      } else {
        setSingleFormApplications([]);
      }
    } catch (err: any) {
      console.error('Error loading applications:', err);
      setSingleFormApplications([]);
    }
  };

  const fetchIntegrationGroups = async () => {
    try {
      const response = await authFetch('/api/application-dependencies/lookup/integration-groups?scope=planning');
      const result = await response.json();
      if (result.success) {
        setIntegrationGroups(result.data || []);
      } else {
        setIntegrationGroups([]);
      }
    } catch (err: any) {
      console.error('Error loading integration groups:', err);
      setIntegrationGroups([]);
    }
  };

  const fetchScheduleData = async () => {
    try {
      const response = await authFetch('/api/roadmap-schedule');
      const result = await response.json();
      if (result.success && Array.isArray(result.data)) {
        setScheduleData(result.data);
      } else {
        setScheduleData([]);
      }
    } catch (err: any) {
      console.error('Error loading schedule data:', err);
      setScheduleData([]);
    }
  };

  // Check if a record is read-only based on deployment status
  // Records are read-only if their application is In Development, Deployed, or On Hold
  const isRecordReadOnly = (record: ResourceAllocationRecord): boolean => {
    const readOnlyStatuses = ['In Development', 'Deployed', 'On Hold'];

    // Check by application ID - use deployment_status as source of truth
    const appSchedule = scheduleData.find(s => s.application_id === record.application_id_code);
    if (appSchedule && appSchedule.deployment_status && readOnlyStatuses.includes(appSchedule.deployment_status)) {
      return true;
    }

    return false;
  };

  // Get the status for display - use deployment_status as source of truth
  const getRecordStatus = (record: ResourceAllocationRecord): string | null => {
    const appSchedule = scheduleData.find(s => s.application_id === record.application_id_code);
    if (appSchedule && appSchedule.deployment_status) {
      return appSchedule.deployment_status;
    }
    return null;
  };

  // Check if an application can be added (only applications not yet in development/deployed)
  const isApplicationEditable = (applicationId: string): boolean => {
    const appSchedule = scheduleData.find(s => s.application_id === applicationId);
    // If no schedule exists or no deployment status, it's editable
    if (!appSchedule || !appSchedule.deployment_status) return true;
    // Only editable if not In Development, Deployed, or On Hold
    const readOnlyStatuses = ['In Development', 'Deployed', 'On Hold'];
    return !readOnlyStatuses.includes(appSchedule.deployment_status);
  };

  // Filter applications to only show editable ones in the add forms
  const editableApplications = applications.filter(app => isApplicationEditable(app.application_id));
  const editableSingleFormApplications = singleFormApplications.filter(app => isApplicationEditable(app.application_id));

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadFile(file);
    setShowUploadModal(true);
    
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      const lines = text.split('\n').filter(line => line.trim());
      const headers = lines[0]!.split(',').map(h => h.trim());
      
      const data = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i]!.split(',').map(v => v.trim());
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
      
      setPreviewData(data);
    };
    
    reader.readAsText(file);
  };

  const handleBulkImport = async () => {
    try {
      const response = await authFetch('/api/resource-allocation/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(previewData)
      });
      
      const data = await response.json();
      
      if (data.imported > 0) {
        setSuccess(`Successfully imported ${data.imported} resource allocation records`);
        fetchRecords();
      }
      
      if (data.errors && data.errors.length > 0) {
        const errorMessage = data.errors.join('\n');
        setError(`Import completed with errors:\n${errorMessage}`);
      }
      
      setShowUploadModal(false);
      setPreviewData([]);
      setUploadFile(null);
    } catch (err: any) {
      setError(err.message || 'Failed to import data');
    }

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const downloadTemplate = () => {
    const template = `Grouping ID,Application ID/Code,Phase Name,Resource Pool ID,Allocated FTE
IG001,APP001,Assessment_Planning,1001,0.5
IG001,APP001,Design_Architecture,1002,1.0
IG002,APP002,Migration_Execution,1003,2.5
IG002,APP003,Testing_Validation,1004,1.5
IG003,APP004,GoLive_Optimization,1005,0.75

Available Phase Names: ${STANDARD_PHASES.join(', ')}`;

    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'resource_allocation_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const downloadData = () => {
    if (records.length === 0) {
      notify('No data to download', 'info');
      return;
    }

    // Match template format: Grouping ID,Application ID/Code,Phase Name,Resource Pool ID,Allocated FTE
    const headers = ['Grouping ID', 'Application ID/Code', 'Phase Name', 'Resource Pool ID', 'Allocated FTE'];
    const csvRows = [headers.join(',')];

    records.forEach(record => {
      const row = [
        `"${(record.integration_group_id || record.grouping_id || '').replace(/"/g, '""')}"`,
        `"${(record.application_id_code || '').replace(/"/g, '""')}"`,
        `"${(record.phase_name || '').replace(/"/g, '""')}"`,
        record.resource_pool_id || '',
        record.allocated_fte || 0
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resource_allocation_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = {
        integration_group_id: formData.groupingId || null,
        application_id_code: formData.applicationIdCode,
        phase_name: formData.phaseName,
        resource_pool_id: formData.resourcePoolId ? parseInt(formData.resourcePoolId) : null,
        allocated_fte: formData.allocatedFte ? parseFloat(formData.allocatedFte) : 0
      };

      if (editingRecord) {
        const response = await authFetch(`/api/resource-allocation/${editingRecord.allocation_id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update');
        }
        setSuccess('Resource allocation record updated successfully');
      } else {
        const response = await authFetch('/api/resource-allocation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create');
        }
        setSuccess('Resource allocation record created successfully');
      }

      fetchRecords();
      
      // Clear any error messages
      setError('');
      
      // If editing, close the form and navigate back to main screen
      if (editingRecord) {
        setShowForm(false);
        setEditingRecord(null);
        resetFormData();
      } else {
        // For new records, just clear form but keep modal open
        resetFormData();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to save resource allocation record');
    }
  };

  const resetFormData = () => {
    setFormData({
      groupingId: '',
      applicationIdCode: '',
      phaseName: '',
      resourcePoolId: '',
      allocatedFte: ''
    });
  };

  const resetBulkFormData = () => {
    setBulkFormData({
      groupingId: '',
      applicationIdCode: '',
      resourcePoolId: '',
      allocations: {}
    });
  };

  const handleEdit = (record: ResourceAllocationRecord) => {
    const readOnly = isRecordReadOnly(record);
    setEditingRecord(record);
    setViewOnlyMode(readOnly);
    setFormData({
      groupingId: record.grouping_id || '',
      applicationIdCode: record.application_id_code,
      phaseName: record.phase_name,
      resourcePoolId: record.resource_pool_id?.toString() || '',
      allocatedFte: record.allocated_fte?.toString() || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (await confirm('Are you sure you want to delete this resource allocation record?', { tone: 'destructive' })) {
      try {
        const response = await authFetch(`/api/resource-allocation/${id}`, {
          method: 'DELETE'
        });
        if (!response.ok) throw new Error('Failed to delete');
        setSuccess('Resource allocation record deleted successfully');
        fetchRecords();
      } catch (err: any) {
        setError(err.message || 'Failed to delete resource allocation record');
      }
    }
  };

  const handleAddNew = () => {
    setEditingRecord(null);
    resetFormData();
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setShowBulkForm(false);
    setEditingRecord(null);
    setViewOnlyMode(false);
    resetFormData();
    resetBulkFormData();
    setError('');
  };


  const getResourcePoolInfo = (poolId: number | null) => {
    if (!poolId) return { type: 'N/A', capacity: 'N/A', location: 'N/A' };
    const pool = resourcePools.find(p => p.resource_pool_id === poolId);
    return pool ? {
      type: pool.resource_type,
      capacity: `${pool.available_capacity_percent}%`,
      location: pool.resource_location
    } : { type: 'Unknown', capacity: 'N/A', location: 'N/A' };
  };

  // Enhanced bulk submit function for all phases
  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Validate required fields
    if (!bulkFormData.groupingId || !bulkFormData.applicationIdCode || !bulkFormData.resourcePoolId) {
      setError('Grouping ID, Application ID and Resource Pool are required');
      return;
    }

    // Filter out phases with valid FTE values (including 0)
    const validAllocations = Object.entries(bulkFormData.allocations)
      .filter(([_, fte]) => fte !== '' && !isNaN(parseFloat(fte)) && parseFloat(fte) >= 0)
      .map(([phase, fte]) => ({
        integration_group_id: bulkFormData.groupingId,
        application_id_code: bulkFormData.applicationIdCode,
        phase_name: phase,
        resource_pool_id: parseInt(bulkFormData.resourcePoolId),
        allocated_fte: parseFloat(fte)
      }));

    if (validAllocations.length === 0) {
      setError('Please enter valid FTE values (including 0) for at least one phase');
      return;
    }

    try {
      // Create multiple records
      for (const allocation of validAllocations) {
        const response = await authFetch('/api/resource-allocation', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(allocation)
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Failed to create ${allocation.phase_name}: ${errorData.error}`);
        }
      }

      setSuccess(`Successfully created ${validAllocations.length} resource allocation records`);
      fetchRecords();
      resetBulkFormData();
      setError('');
    } catch (err: any) {
      setError(err.message || 'Failed to save resource allocations');
    }
  };

  const filteredRecords = records.filter(record => {
    const appMatch = filterApplicationCode === '' ||
      record.application_id_code.toLowerCase().includes(filterApplicationCode.toLowerCase()) ||
      record.application_name?.toLowerCase().includes(filterApplicationCode.toLowerCase());
    const phaseMatch = filterPhase === '' || record.phase_name === filterPhase;
    const groupingMatch = filterGroupingId === '' ||
      record.grouping_id?.toLowerCase().includes(filterGroupingId.toLowerCase()) ||
      record.group_name?.toLowerCase().includes(filterGroupingId.toLowerCase());
    const resourcePoolMatch = filterResourcePool === '' ||
      record.resource_pool_id?.toString() === filterResourcePool ||
      resourcePools.find(p => p.resource_pool_id === record.resource_pool_id)?.resource_type.toLowerCase().includes(filterResourcePool.toLowerCase());

    return appMatch && phaseMatch && groupingMatch && resourcePoolMatch;
  });

  // DW: the custom application dropdown — the wrapper (.custom-dropdown) is the field.
  const dropdownTrigger = (enabled: boolean): React.CSSProperties => ({
    height: '100%',
    cursor: enabled ? 'pointer' : 'not-allowed',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  });
  const dropdownMenu: React.CSSProperties = {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: WS_COLOR.surface,
    border: `1px solid ${WS_COLOR.border}`,
    borderRadius: WS_RADIUS.field,
    boxShadow: WS_COLOR.menuShadow,
    zIndex: 1000,
    maxHeight: '200px',
    overflowY: 'auto'
  };
  const dropdownOption = (selected: boolean): React.CSSProperties => ({
    padding: '12px 16px',
    cursor: 'pointer',
    ...wsType('table'),
    borderBottom: `1px solid ${WS_COLOR.border}`,
    ...(selected ? wsTone('info') : { background: WS_COLOR.surface, color: WS_COLOR.text }),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  });
  const dispositionChip = (disposition: string | undefined): React.CSSProperties => ({
    ...WS_CHIP,
    backgroundColor: getDispositionColor(disposition).bg,
    color: getDispositionColor(disposition).color
  });

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <HelpPanel help={ResourceAllocationHelp} />
      {/* Header - Fixed/Sticky */}
      <PageHeader title={<>Resource Allocation Planning</>} subtitle={<>Manage resource allocation for application migration phases</>} />
      <div className="page-header__actions" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
<button
              className="btn btn-primary"
              onClick={() => setShowBulkForm(true)}
              title="Allocate one resource pool across all 5 phases"
            >
              <Icon icon={icons.Plus} size={20} /> Add Resource Allocation
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={handleAddNew}
              title="Create allocation for one phase only"
            >
              <Icon icon={icons.Pencil} size={20} /> Single Phase Only
            </button>
            <button
              className="btn btn-outline-info"
              onClick={downloadTemplate}
              title="Download CSV template for bulk import"
            >
              <Icon icon={icons.ClipboardList} size={20} /> Download Template
            </button>
            <button
              className="btn btn-outline-primary"
              onClick={() => {
                if (fileInputRef.current) {
                  fileInputRef.current.value = '';
                }
                fileInputRef.current?.click();
              }}
              title="Import allocations from CSV"
            >
              <Icon icon={icons.Upload} size={20} /> Upload File
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            {records.length > 0 && (
              <button
                className="btn btn-outline-success"
                onClick={downloadData}
                title="Download current data as CSV"
              >
                <Icon icon={icons.Download} size={20} /> Download Data
              </button>
            )}
      </div>

      {/* Scrollable Content Area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>

      {loading && (
        <div className="md-loading" style={{ padding: '40px', textAlign: 'center' }}>
          Loading resource allocation records...
        </div>
      )}

      {!loading && (
        <>

      {/* Filter Section - Sticky */}
      {records.length > 0 && (
        <div className="md-card" style={{ marginBottom: '16px', padding: '16px', position: 'sticky', top: 0, zIndex: 100, backgroundColor: WS_COLOR.surface }}>
          {/* Row 1: Application, Grouping ID, Phase, Resource Pool */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <input
              type="text"
              value={filterApplicationCode}
              onChange={(e) => setFilterApplicationCode(e.target.value)}
              placeholder="Enter application code..."
              style={{ minWidth: '150px' }}
            />
            <input
              type="text"
              value={filterGroupingId}
              onChange={(e) => setFilterGroupingId(e.target.value)}
              placeholder="Enter grouping ID..."
              style={{ minWidth: '150px' }}
            />
            <label style={{ minWidth: '80px' }}>Phase:</label>
            <select
              value={filterPhase}
              onChange={(e) => setFilterPhase(e.target.value)}
              style={{ minWidth: '150px' }}
            >
              <option value="">All Phases</option>
              {STANDARD_PHASES.map(phase => (
                <option key={phase} value={phase}>
                  {phase.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
            <label style={{ minWidth: '120px' }}>Resource Pool:</label>
            <select
              value={filterResourcePool}
              onChange={(e) => setFilterResourcePool(e.target.value)}
              style={{ minWidth: '150px' }}
            >
              <option value="">All Resource Pools</option>
              {resourcePools.map(pool => (
                <option key={pool.resource_pool_id} value={pool.resource_pool_id}>
                  [{pool.resource_pool_id}] {pool.resource_type}
                </option>
              ))}
            </select>
          </div>

          {/* Row 2: Clear Filters, Count. DEVIATION (owner ruling 2026-11-03): the source's
              Status filter (default "Need Approval") is removed — allocations are approved as
              entered, so the roadmap approval status no longer filters this screen. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {(filterApplicationCode || filterPhase || filterGroupingId || filterResourcePool) && (
              <button
                className="btn"
                onClick={() => {
                  setFilterApplicationCode('');
                  setFilterPhase('');
                  setFilterGroupingId('');
                  setFilterResourcePool('');
                }}
              >
                Clear Filters
              </button>
            )}
            <span style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginLeft: 'auto' }}>
              Showing {filteredRecords.length} of {records.length} allocations
            </span>
          </div>
        </div>
      )}

      {/* Main Content Card */}
      <div className="md-card" style={{ flex: '1', overflow: 'visible', minHeight: 'auto' }}>
        {records.length === 0 ? (
          <div className="md-empty-state" style={{ padding: '40px' }}>
            <div className="md-empty-icon"><Icon icon={icons.BarChart3} size={24} /></div>
            <h3>No Resource Allocation Records Found</h3>
            <p>Use the action cards above to add resource allocations or upload data from a CSV file</p>
          </div>
        ) : (
          <div className="md-table-container" style={{ overflowY: 'visible', height: 'auto', maxHeight: 'none' }}>
            <table className="md-table">
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px', whiteSpace: 'nowrap' }}>Grouping ID</th>
                  <th style={{ textAlign: 'left', padding: '8px', whiteSpace: 'nowrap' }}>Application</th>
                  <th style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>Phase</th>
                  <th style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>Resource Pool</th>
                  <th style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>Location</th>
                  <th style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>FTE</th>
                  <th style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map(record => {
                  const poolInfo = getResourcePoolInfo(record.resource_pool_id);
                  const readOnly = isRecordReadOnly(record);
                  return (
                    <tr key={record.allocation_id} style={{ backgroundColor: readOnly ? WS_COLOR.surfaceSubtle : WS_COLOR.surface }}>
                      <td style={{ textAlign: 'left', padding: '8px', whiteSpace: 'nowrap' }}>
                        <div>
                          <div style={{ fontWeight: 600, textAlign: 'left' }}>{record.grouping_id || '-'}</div>
                          {record.group_name && (
                            <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, textAlign: 'left' }}>{record.group_name}</div>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'left', padding: '8px', whiteSpace: 'nowrap' }}>
                        <div>
                          <div style={{ fontWeight: 600, textAlign: 'left', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {record.application_id_code}
                          </div>
                          {record.application_name && (
                            <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, textAlign: 'left' }}>{record.application_name}</div>
                          )}
                        </div>
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>
                        {record.phase_name}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 600 }}>{poolInfo.type}</div>
                      </td>
                      <td style={{
                        textAlign: 'center',
                        padding: '8px',
                        whiteSpace: 'nowrap'
                      }}>
                        <span style={{ ...WS_CHIP, ...wsTone(poolInfo.location === 'On-Site' ? 'info' : 'success') }}>
                          {poolInfo.location}
                        </span>
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                        {record.allocated_fte}
                      </td>
                      <td style={{ textAlign: 'center', padding: '8px', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                          <IconButton
                            icon={icons.Eye}
                            label="View allocation details"
                            title="View allocation details"
                            onClick={() => handleEdit(record)}
                          />
                          {!readOnly && (
                            <>
                              <IconButton
                                icon={icons.Pencil}
                                label="Edit allocation"
                                title="Edit allocation"
                                onClick={() => handleEdit(record)}
                              />
                              <IconButton
                                icon={icons.Trash2}
                                label="Delete allocation"
                                title="Delete allocation"
                                onClick={() => handleDelete(record.allocation_id)}
                              />
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Resource Allocation Form — a right-side panel (Decision Workspace Sheet, single width). */}
      <Sheet
        open={showForm || showBulkForm}
        title={showBulkForm ? 'Add New Resource Allocation' : viewOnlyMode ? 'View Resource Allocation (Read Only)' : editingRecord ? 'Edit Resource Allocation' : 'Add Single Phase Allocation'}
        onClose={handleCloseForm}
        width={531}
        actions={
          showBulkForm ? (
            <>
              <button type="button" className="btn" onClick={handleCloseForm}>
                Cancel
              </button>
              <button type="submit" form="ra-bulk-form" className="btn btn-primary">
                Create {Object.entries(bulkFormData.allocations).filter(([_, fte]) => fte && parseFloat(fte) > 0).length || 'Multi'} Records
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn" onClick={handleCloseForm}>
                {viewOnlyMode ? 'Close' : 'Cancel'}
              </button>
              {!viewOnlyMode && (
                <button type="submit" form="ra-single-form" className="btn btn-primary">
                  {editingRecord ? 'Update Resource Allocation' : 'Create Resource Allocation'}
                </button>
              )}
            </>
          )
        }
      >
              {showBulkForm ? (
                // Bulk Phase Allocation Form
                <form id="ra-bulk-form" onSubmit={handleBulkSubmit}>
                  {error && (
                    <div style={{ marginBottom: '16px' }}>
                      <Banner tone="error">{error}</Banner>
                    </div>
                  )}

                  <div className="md-form-section" style={{marginBottom: '24px'}}>
                    <h3 style={{margin: '0 0 16px 0'}}>Application Information</h3>

                    {/* Grouping ID Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{marginBottom: '8px', display: 'block'}}>Integration Group *</label>
                      <select
                        className="md-select"
                        style={{
                          width: '100%',
                          cursor: 'pointer'
                        }}
                        value={bulkFormData.groupingId}
                        onChange={(e) => {
                          setBulkFormData({
                            ...bulkFormData,
                            groupingId: e.target.value,
                            applicationIdCode: '' // Reset application when group changes
                          });
                          setShowAppDropdown(false);
                        }}
                        required
                      >
                        <option value="">Select Integration Group</option>
                        {integrationGroups.map(group => (
                          <option key={group.integration_group_id} value={group.integration_group_id}>
                            {group.integration_group_id} - {group.integration_group_description} ({group.application_count} apps)
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Application ID Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{marginBottom: '8px', display: 'block'}}>Application *</label>
                      <div className="custom-dropdown" style={{ position: 'relative', backgroundColor: !bulkFormData.groupingId ? WS_COLOR.surfaceSubtle : WS_COLOR.surface }}>
                        <div
                          onClick={() => {
                            if (bulkFormData.groupingId) {
                              setShowAppDropdown(!showAppDropdown);
                            }
                          }}
                          style={dropdownTrigger(!!bulkFormData.groupingId)}
                        >
                          <span style={{ color: bulkFormData.applicationIdCode ? WS_COLOR.text : WS_COLOR.textMuted, display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            {bulkFormData.applicationIdCode && editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)
                              ? (
                                <>
                                  <span>{editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)!.application_id} - {editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)!.description || editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)!.application_name}</span>
                                  {editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)!.disposition && (
                                    <span style={dispositionChip(editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)!.disposition)}>
                                      {editableApplications.find(a => a.application_id === bulkFormData.applicationIdCode)!.disposition}
                                    </span>
                                  )}
                                </>
                              )
                              : (bulkFormData.groupingId ? 'Select Application' : 'Select Integration Group first')}
                          </span>
                          {bulkFormData.groupingId && <Icon icon={icons.ChevronDown} size={16} />}
                        </div>
                        {showAppDropdown && bulkFormData.groupingId && (
                          <div style={dropdownMenu}>
                            {editableApplications.length === 0 ? (
                              <div style={{ padding: '12px 16px', color: WS_COLOR.textMuted, ...wsType('table') }}>
                                No editable applications available for this group
                              </div>
                            ) : (
                              editableApplications.map(app => (
                                <div
                                  key={app.application_id}
                                  onClick={() => {
                                    setBulkFormData({...bulkFormData, applicationIdCode: app.application_id});
                                    setShowAppDropdown(false);
                                  }}
                                  style={dropdownOption(bulkFormData.applicationIdCode === app.application_id)}
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = WS_COLOR.surfaceSubtle)}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = bulkFormData.applicationIdCode === app.application_id ? WS_STATUS.info.fill : WS_COLOR.surface)}
                                >
                                  <span><strong>{app.application_id}</strong> - {app.description || app.application_name}</span>
                                  {app.disposition && (
                                    <span style={{ ...dispositionChip(app.disposition), marginLeft: '8px' }}>
                                      {app.disposition}
                                    </span>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Resource Pool Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{marginBottom: '8px', display: 'block'}}>Resource Pool *</label>
                      <select
                        className="md-select"
                        style={{
                          width: '100%',
                          cursor: 'pointer'
                        }}
                        value={bulkFormData.resourcePoolId}
                        onChange={(e) => setBulkFormData({...bulkFormData, resourcePoolId: e.target.value})}
                        required
                      >
                        <option value="">Select Resource Pool</option>
                        {resourcePools.map(pool => (
                          <option key={pool.resource_pool_id} value={pool.resource_pool_id}>
                            {pool.resource_type} - {pool.resource_location}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="md-form-section">
                    <h3 style={{ marginBottom: '16px' }}>FTE Allocation by Phase</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
                      {STANDARD_PHASES.map(phase => (
                        <div key={phase} className="md-form-group" style={{ marginBottom: '8px' }}>
                          <label style={{
                            ...wsType('meta'),
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'flex-end',
                            marginBottom: '4px',
                            height: '32px'
                          }}>
                            {phase.replace(/_/g, ' ')}
                          </label>
                          <input
                            type="number"
                            className="md-input no-spinners"
                            value={bulkFormData.allocations[phase] || ''}
                            onChange={(e) => setBulkFormData({
                              ...bulkFormData,
                              allocations: {
                                ...bulkFormData.allocations,
                                [phase]: e.target.value
                              }
                            })}
                            placeholder="0, 0.25, 1.0, 2.5"
                            min="0"
                            max="10"
                            step="0.01"
                            style={{
                              width: '100%',
                              padding: '0 8px',
                              MozAppearance: 'textfield'
                            }}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Show allocation summary */}
                    {Object.keys(bulkFormData.allocations).some(phase => bulkFormData.allocations[phase] !== '') && (
                      <div style={{
                        marginTop: '12px',
                        padding: '8px',
                        backgroundColor: WS_COLOR.surfaceSubtle,
                        borderRadius: WS_RADIUS.field,
                        border: `1px solid ${WS_COLOR.border}`
                      }}>
                        <div style={{ ...wsType('meta'), fontWeight: 600, marginBottom: '4px' }}>
                          Records to Create ({Object.entries(bulkFormData.allocations).filter(([_, fte]) => fte !== '' && !isNaN(parseFloat(fte))).length} individual):
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '2px' }}>
                          {Object.entries(bulkFormData.allocations)
                            .filter(([_, fte]) => fte !== '' && !isNaN(parseFloat(fte)))
                            .map(([phase, fte]) => (
                              <div key={phase} style={{ ...wsType('meta'), color: WS_COLOR.textMuted, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ display: 'inline-flex', color: parseFloat(fte) === 0 ? WS_STATUS.warning.ink : WS_STATUS.success.ink }}>
                                  <Icon icon={parseFloat(fte) === 0 ? icons.Pencil : icons.FileText} size={16} />
                                </span>
                                <span style={{ color: parseFloat(fte) === 0 ? WS_STATUS.warning.ink : WS_COLOR.text }}>
                                  {phase.replace(/_/g, ' ')}: {fte} {parseFloat(fte) === 0 ? '(placeholder)' : ''}
                                </span>
                              </div>
                            ))
                          }
                        </div>
                        <div style={{ ...wsType('meta'), ...wsTone('success'), fontWeight: 600, marginTop: '6px', padding: '4px', borderRadius: WS_RADIUS.chip, display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <Icon icon={icons.CheckCircle2} size={16} /> {Object.entries(bulkFormData.allocations).filter(([_, fte]) => fte !== '' && !isNaN(parseFloat(fte))).length} records • {Object.values(bulkFormData.allocations)
                            .filter(fte => fte !== '' && !isNaN(parseFloat(fte)))
                            .reduce((sum, fte) => sum + parseFloat(fte), 0)
                            .toFixed(1)} total FTE
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Cancel and Create Records are in the panel's fixed action row (Sheet `actions`). */}
                </form>
              ) : (
                // Single Allocation Form
                <form id="ra-single-form" onSubmit={handleSubmit}>
                  {error && (
                    <div style={{ marginBottom: '16px' }}>
                      <Banner tone="error">{error}</Banner>
                    </div>
                  )}

                  <div className="md-form-section" style={{marginBottom: '24px'}}>
                    <h3 style={{margin: '0 0 16px 0'}}>Application Information</h3>

                    {/* Integration Group Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{marginBottom: '8px', display: 'block'}}>Integration Group *</label>
                      <select
                        className="md-select"
                        style={{
                          width: '100%',
                          backgroundColor: viewOnlyMode ? WS_COLOR.surfaceSubtle : WS_COLOR.surface,
                          cursor: viewOnlyMode ? 'not-allowed' : 'pointer'
                        }}
                        value={formData.groupingId}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            groupingId: e.target.value,
                            applicationIdCode: '' // Reset application when group changes
                          });
                          setShowSingleAppDropdown(false);
                        }}
                        required
                        disabled={viewOnlyMode}
                      >
                        <option value="">Select Integration Group</option>
                        {integrationGroups.map(group => (
                          <option key={group.integration_group_id} value={group.integration_group_id}>
                            {group.integration_group_id} - {group.integration_group_description} ({group.application_count} apps)
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Application ID Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{marginBottom: '8px', display: 'block'}}>Application *</label>
                      <div className="custom-dropdown" style={{ position: 'relative', backgroundColor: (!formData.groupingId || viewOnlyMode) ? WS_COLOR.surfaceSubtle : WS_COLOR.surface }}>
                        <div
                          onClick={() => {
                            if (formData.groupingId && !viewOnlyMode) {
                              setShowSingleAppDropdown(!showSingleAppDropdown);
                            }
                          }}
                          style={dropdownTrigger(!!formData.groupingId && !viewOnlyMode)}
                        >
                          <span style={{ color: formData.applicationIdCode ? WS_COLOR.text : WS_COLOR.textMuted, display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            {formData.applicationIdCode && singleFormApplications.find(a => a.application_id === formData.applicationIdCode)
                              ? (
                                <>
                                  <span>{singleFormApplications.find(a => a.application_id === formData.applicationIdCode)!.application_id} - {singleFormApplications.find(a => a.application_id === formData.applicationIdCode)!.description || singleFormApplications.find(a => a.application_id === formData.applicationIdCode)!.application_name}</span>
                                  {singleFormApplications.find(a => a.application_id === formData.applicationIdCode)!.disposition && (
                                    <span style={dispositionChip(singleFormApplications.find(a => a.application_id === formData.applicationIdCode)!.disposition)}>
                                      {singleFormApplications.find(a => a.application_id === formData.applicationIdCode)!.disposition}
                                    </span>
                                  )}
                                </>
                              )
                              : (formData.groupingId ? 'Select Application' : 'Select Integration Group first')}
                          </span>
                          {formData.groupingId && !viewOnlyMode && <Icon icon={icons.ChevronDown} size={16} />}
                        </div>
                        {showSingleAppDropdown && formData.groupingId && !viewOnlyMode && (
                          <div style={dropdownMenu}>
                            {editableSingleFormApplications.length === 0 ? (
                              <div style={{ padding: '12px 16px', color: WS_COLOR.textMuted, ...wsType('table') }}>
                                No editable applications available for this group
                              </div>
                            ) : (
                              editableSingleFormApplications.map(app => (
                                <div
                                  key={app.application_id}
                                  onClick={() => {
                                    setFormData({...formData, applicationIdCode: app.application_id});
                                    setShowSingleAppDropdown(false);
                                  }}
                                  style={dropdownOption(formData.applicationIdCode === app.application_id)}
                                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = WS_COLOR.surfaceSubtle)}
                                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = formData.applicationIdCode === app.application_id ? WS_STATUS.info.fill : WS_COLOR.surface)}
                                >
                                  <span><strong>{app.application_id}</strong> - {app.description || app.application_name}</span>
                                  {app.disposition && (
                                    <span style={{ ...dispositionChip(app.disposition), marginLeft: '8px' }}>
                                      {app.disposition}
                                    </span>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Phase Name Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{marginBottom: '8px', display: 'block'}}>Phase Name *</label>
                      <select
                        className="md-select"
                        style={{
                          width: '100%',
                          backgroundColor: viewOnlyMode ? WS_COLOR.surfaceSubtle : WS_COLOR.surface,
                          cursor: viewOnlyMode ? 'not-allowed' : 'pointer'
                        }}
                        value={formData.phaseName}
                        onChange={(e) => setFormData({...formData, phaseName: e.target.value})}
                        required
                        disabled={viewOnlyMode}
                      >
                        <option value="">Select a phase...</option>
                        {STANDARD_PHASES.map(phase => (
                          <option key={phase} value={phase}>
                            {phase.replace(/_/g, ' ')}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Resource Pool Dropdown */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{marginBottom: '8px', display: 'block'}}>Resource Pool *</label>
                      <select
                        className="md-select"
                        style={{
                          width: '100%',
                          backgroundColor: viewOnlyMode ? WS_COLOR.surfaceSubtle : WS_COLOR.surface,
                          cursor: viewOnlyMode ? 'not-allowed' : 'pointer'
                        }}
                        value={formData.resourcePoolId}
                        onChange={(e) => setFormData({...formData, resourcePoolId: e.target.value})}
                        required
                        disabled={viewOnlyMode}
                      >
                        <option value="">Select Resource Pool</option>
                        {resourcePools.map(pool => (
                          <option key={pool.resource_pool_id} value={pool.resource_pool_id}>
                            {pool.resource_type} - {pool.resource_location}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Allocated FTE */}
                    <div className="md-form-group" style={{marginBottom: '20px'}}>
                      <label style={{marginBottom: '8px', display: 'block'}}>Allocated FTE *</label>
                      <input
                        type="number"
                        className="md-input"
                        style={{
                          width: '100%',
                          backgroundColor: viewOnlyMode ? WS_COLOR.surfaceSubtle : WS_COLOR.surface
                        }}
                        value={formData.allocatedFte}
                        onChange={(e) => setFormData({...formData, allocatedFte: e.target.value})}
                        placeholder="Full-Time Equivalent (e.g., 0.25, 0.5, 1.0)"
                        min="0"
                        max="10"
                        step="0.01"
                        required
                        disabled={viewOnlyMode}
                      />
                    </div>
                  </div>

                  {/* Cancel/Close and Create/Update are in the panel's fixed action row (Sheet `actions`). */}
                </form>
              )}
      </Sheet>

      {/* Upload — a right-side panel (Decision Workspace Sheet, single width). */}
      <Sheet
        open={showUploadModal}
        title="Preview Import Data"
        onClose={() => setShowUploadModal(false)}
        width={531}
        actions={
          <>
            <button type="button" className="btn" onClick={() => setShowUploadModal(false)}>
              Cancel
            </button>
            {previewData.length > 0 && (
              <button type="button" className="btn btn-primary" onClick={handleBulkImport}>
                Import {previewData.length} Records
              </button>
            )}
          </>
        }
      >
              {previewData.length > 0 ? (
                <div>
                  <p>Found {previewData.length} records. Preview:</p>
                  <div style={{ maxHeight: '300px', overflow: 'auto', border: `1px solid ${WS_COLOR.border}`, borderRadius: WS_RADIUS.field }}>
                    <table style={{ width: '100%', ...wsType('meta') }}>
                      <thead style={{ backgroundColor: WS_COLOR.surfaceSubtle }}>
                        <tr>
                          {Object.keys(previewData[0] || {}).map(key => (
                            <th key={key} style={{ fontWeight: 600, padding: '8px', textAlign: 'left', borderBottom: `1px solid ${WS_COLOR.border}` }}>
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {previewData.slice(0, 5).map((row, index) => (
                          <tr key={index}>
                            {Object.values(row).map((value: any, cellIndex) => (
                              <td key={cellIndex} style={{ padding: '8px', borderBottom: `1px solid ${WS_COLOR.border}` }}>
                                {value}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {previewData.length > 5 && (
                    <p style={{ ...wsType('meta'), color: WS_COLOR.textMuted }}>
                      ... and {previewData.length - 5} more records
                    </p>
                  )}
                </div>
              ) : (
                <p>No data found in the uploaded file.</p>
              )}
      </Sheet>

      {/* Success/Error Messages */}
      {success && (
        <div className="md-toast md-toast--success">
          {success}
          <button onClick={() => setSuccess('')} aria-label="Close"><Icon icon={icons.X} size={16} /></button>
        </div>
      )}
      {error && (
        <div className="md-toast md-toast--error">
          {error}
          <button onClick={() => setError('')} aria-label="Close"><Icon icon={icons.X} size={16} /></button>
        </div>
      )}
      </>
      )}
      </div>
      {dialogs}
    </div>
  );
};

export default ResourceAllocation;